import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { bookSlotSchema } from "@/lib/calculator-schemas";
import type { Slot } from "@/lib/booking";

export const getAvailableSlots = createServerFn({ method: "GET" }).handler(async (): Promise<Slot[]> => {
  const { listOpenSlots } = await import("@/lib/booking.server");
  return listOpenSlots();
});

export const createAppointment = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => bookSlotSchema.parse(input))
  .handler(async ({ data }) => {
    const { bookSlot, notifyBooking } = await import("@/lib/booking.server");
    const appointment = await bookSlot(data);
    if (data.address) {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        await supabaseAdmin.from("leads").update({ address: data.address }).eq("id", data.leadId).is("address", null);
      } catch (e) {
        console.error("[createAppointment] address save failed", e);
      }
    }
    try {
      await notifyBooking(data.leadId, appointment.label);
    } catch (e) {
      console.error("[createAppointment] notification failed", e);
    }
    return appointment;
  });

export const adminListAppointments = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ scope: z.enum(["upcoming", "past", "all"]).default("upcoming") }).parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("@/lib/calculator-admin.server");
    await assertAdmin(context);
    const { listAppointments } = await import("@/lib/booking.server");
    return listAppointments(data.scope);
  });

export const adminSetAppointmentStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["scheduled", "confirmed", "completed", "cancelled", "no_show"]),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("@/lib/calculator-admin.server");
    await assertAdmin(context);
    const { setAppointmentStatus } = await import("@/lib/booking.server");
    await setAppointmentStatus(data.id, data.status);
    return { ok: true };
  });
